import { useId, useState } from 'react';
import type { ContinuousMonitor } from '../api';
import { Callout } from './primitives';
import { relativeTime } from './project/projectHealth';

/** One statement that was running when the check looked, reduced to its shape by the agent. */
export interface RunningStatement {
  seconds: number;
  table: string;
  state: string;
  shape: string;
}

/** What the slow queries check reads of the store's database (CO-156). */
export interface SlowQueryReading {
  version: string;
  /** The server's own setting: whether it records slow queries in its log. */
  slowQueryLog: 'on' | 'off' | 'unknown';
  /** After how many seconds the server counts a query as slow. */
  longQueryTimeSeconds?: number;
  slowQueriesTotal?: number;
  uptimeSeconds?: number;
  /** Counted as slow since the check before; absent for the first reading and after a restart. */
  sinceLast?: number;
  windowSeconds?: number;
  restarted: boolean;
  /** Statements over this many seconds are listed. */
  thresholdSeconds: number;
  running: RunningStatement[];
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The reading in a monitor's last result, or null when it has none (never read, or the database could not be read). */
export function slowQueryReadingOf(metrics: Record<string, unknown> | undefined): SlowQueryReading | null {
  if (!metrics || num(metrics.slowQueriesTotal) === undefined) return null;
  const running = Array.isArray(metrics.running)
    ? metrics.running.flatMap((raw): RunningStatement[] => {
        const r = raw as Record<string, unknown>;
        const seconds = num(r?.seconds);
        return seconds === undefined ? [] : [{ seconds, table: text(r.table), state: text(r.state), shape: text(r.shape) }];
      })
    : [];
  const log = text(metrics.slowQueryLog);
  return {
    version: text(metrics.version),
    slowQueryLog: log === 'on' || log === 'off' ? log : 'unknown',
    longQueryTimeSeconds: num(metrics.longQueryTimeSeconds),
    slowQueriesTotal: num(metrics.slowQueriesTotal),
    uptimeSeconds: num(metrics.uptimeSeconds),
    sinceLast: num(metrics.slowQueriesSinceLastCheck),
    windowSeconds: num(metrics.windowSeconds),
    restarted: metrics.restarted === true,
    thresholdSeconds: num(metrics.thresholdSeconds) ?? 5,
    running,
  };
}

/** Seconds the way a person reads them: "42 s", "3 min", "2 h". */
export function secondsInWords(seconds: number): string {
  if (seconds < 120) return `${Math.round(seconds)} s`;
  if (seconds < 7200) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)} h`;
}

function windowWords(seconds: number | undefined): string {
  if (seconds === undefined || seconds < 90) return 'minute';
  return `${Math.round(seconds / 60)} min`;
}

/**
 * SlowQueriesDetails is the "Slow database queries" check opened up (CO-156):
 * what the server counted as slow, and the statements running right now with
 * their values removed (so a customer's name or address is never shown), in
 * words a person who is not a database administrator can act on. It says
 * what the check cannot see, because "nothing is slow" means more than it
 * should when the check only looks every couple of minutes.
 */
export function SlowQueriesDetails({ monitor }: { monitor: ContinuousMonitor }) {
  const panelId = useId();
  const reading = slowQueryReadingOf(monitor.last_result?.metrics);
  const [open, setOpen] = useState(monitor.status === 'warning');
  if (!monitor.last_result) return null;

  const warning = monitor.status === 'warning';
  const slowAfter = reading?.longQueryTimeSeconds;
  const everyQueryCounts = slowAfter !== undefined && slowAfter < 1;

  return (
    <div className="slow-queries" data-testid="slow-queries">
      <button
        type="button"
        className="button button--secondary button--small slow-queries__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Hide details' : 'Show details'}
      </button>
      {open ? (
        <div id={panelId} className="slow-queries__panel" role="region" aria-label="Slow database queries in detail">
          {!reading ? (
            <p className="slow-queries__note" data-testid="slow-queries-unread">
              {monitor.last_result.message
                ? `${monitor.last_result.message}. `
                : 'The database’s slow query counters could not be read at the last check. '}
              The Database check says whether the database is answering at all.
            </p>
          ) : (
            <>
              <section aria-labelledby={`${panelId}-running`}>
                <h3 id={`${panelId}-running`} className="slow-queries__heading">
                  Running now
                </h3>
                {reading.running.length === 0 ? (
                  <p className="slow-queries__note" data-testid="slow-queries-none">
                    Nothing running for {secondsInWords(reading.thresholdSeconds)} or more
                  </p>
                ) : (
                  <>
                    <p className="slow-queries__note">
                      Running for {secondsInWords(reading.thresholdSeconds)} or more, longest first · values removed, <code translate="no">?</code> stands
                      for what was asked about
                    </p>
                    <ul className="slow-queries__list" aria-label="Statements running now, with their values removed">
                      {reading.running.map((statement, index) => (
                        <li key={`${index}:${statement.seconds}:${statement.shape}`} className="slow-queries__item">
                          <p className="slow-queries__lead">
                            <strong className="tabular-nums">{secondsInWords(statement.seconds)}</strong>
                            {statement.table ? (
                              <span>
                                on <code translate="no">{statement.table}</code>
                              </span>
                            ) : (
                              <span className="muted">table not named</span>
                            )}
                            {statement.state ? <span className="slow-queries__state">{statement.state}</span> : null}
                          </p>
                          <code translate="no" className="slow-queries__shape">{statement.shape}</code>
                        </li>
                      ))}
                    </ul>
                    {warning ? (
                      <p className="slow-queries__note">
                        A statement that runs this long usually has no index it can use, reads far more rows than it needs, or is waiting for another
                        statement. Ask a developer to look at it: the table and the shape above are what to look for in the code.
                      </p>
                    ) : null}
                  </>
                )}
              </section>

              <dl className="slow-queries__facts" aria-label="What the database counted">
                <div>
                  <dt>Counted as slow</dt>
                  <dd className="tabular-nums" data-testid="slow-queries-count">
                    {reading.slowQueriesTotal?.toLocaleString() ?? 'Unknown'} since the database started
                    {reading.sinceLast !== undefined
                      ? `, ${reading.sinceLast.toLocaleString()} in the last ${windowWords(reading.windowSeconds)}`
                      : reading.restarted
                        ? ', and it restarted since the last check'
                        : ', counting from the next check'}
                  </dd>
                </div>
                <div>
                  <dt>Slow means</dt>
                  <dd className="tabular-nums">
                    {slowAfter === undefined
                      ? 'Not reported by the database'
                      : everyQueryCounts
                        ? `Longer than ${secondsInWords(slowAfter)}, so the count says nothing`
                        : `Longer than ${secondsInWords(slowAfter)}`}
                  </dd>
                </div>
                <div>
                  <dt>Slow query log</dt>
                  <dd>{reading.slowQueryLog === 'on' ? 'On' : reading.slowQueryLog === 'off' ? 'Off' : 'Not reported'}</dd>
                </div>
                {reading.version ? (
                  <div>
                    <dt>Database</dt>
                    <dd>{reading.version}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Checked</dt>
                  <dd>{relativeTime(monitor.last_result.checked_at)}</dd>
                </div>
              </dl>

              {reading.slowQueryLog === 'off' ? (
                <Callout tone="info" testId="slow-queries-log-off">
                  The database’s slow query log is off. It is what records which queries were slow after the fact; without it, only the statements running when a
                  check looks can be shown. To turn it on, ask your host or database administrator to set <code translate="no">slow_query_log = 1</code> and{' '}
                  <code translate="no">long_query_time</code> to the seconds you call slow (2 is common). Commerce Ops never changes your database settings.
                </Callout>
              ) : null}

              <p className="slow-queries__note">
                Commerce Ops sees only statements of the store’s own database user, and only the ones running when it looks (every{' '}
                {Math.round(monitor.interval_seconds / 60) || 1} min). A query that ends in between is counted as slow, but its statement is not shown.
              </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
